import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { KeyRound, Code2, ArrowLeft } from 'lucide-react';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CodeBlock = ({ children }) => (
  <pre className="bg-[#121212] text-[#E5E4E0] text-xs rounded-lg p-4 overflow-x-auto font-mono leading-relaxed">
    <code>{children}</code>
  </pre>
);

export default function ApiDocsPage() {
  const { language } = useLanguage();
  const tr = (lv, en) => (language === 'lv' ? lv : en);

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="api-docs-page">
      <div className="max-w-3xl mx-auto px-4 py-12">
        <Link to="/" className="inline-flex items-center gap-1 text-sm text-[#5C5C5C] hover:text-[#D97736] mb-6">
          <ArrowLeft className="w-4 h-4" /> {tr('Atpakaļ', 'Back')}
        </Link>

        <div className="flex items-center gap-3 mb-2">
          <Code2 className="w-7 h-7 text-[#D97736]" />
          <h1 className="font-outfit font-bold text-3xl text-[#121212]">KoLV Headless API</h1>
        </div>
        <p className="text-[#5C5C5C] mb-8">
          {tr('Premium Ultra pārdevēji var būvēt savu veikala priekšpusi (Next.js, Vue, WordPress) un pievilkt savus datus caur šo lasīšanas API.',
              'Premium Ultra sellers can build their own storefront (Next.js, Vue, WordPress) and pull their data via this read-only API.')}
        </p>

        <section className="bg-white border border-[#E5E4E0] rounded-xl p-6 mb-6">
          <h2 className="font-semibold text-lg text-[#121212] flex items-center gap-2 mb-3"><KeyRound className="w-5 h-5 text-[#4A6B53]" /> {tr('Autentifikācija', 'Authentication')}</h2>
          <p className="text-sm text-[#5C5C5C] mb-3">
            {tr('Ģenerē savu API atslēgu sadaļā Veikala dizains → Headless API. Pievieno to katram pieprasījumam galvenē:',
                'Generate your API key under Shop Design → Headless API. Send it with every request header:')}
          </p>
          <CodeBlock>{`X-API-Key: kolv_xxxxxxxxxxxxxxxxxxxx`}</CodeBlock>
        </section>

        <section className="bg-white border border-[#E5E4E0] rounded-xl p-6 mb-6">
          <h2 className="font-semibold text-lg text-[#121212] mb-3">{tr('Galapunkti', 'Endpoints')}</h2>
          <ul className="text-sm font-mono text-[#121212] space-y-2">
            <li><span className="text-[#4A6B53]">GET</span> {API_BASE}/public/v1/shop</li>
            <li><span className="text-[#4A6B53]">GET</span> {API_BASE}/public/v1/listings</li>
            <li className="text-xs text-[#5C5C5C] pl-10">?category= &q= &min_price= &max_price= &region= &sort=newest|price_asc|price_desc &limit= &skip=</li>
            <li><span className="text-[#4A6B53]">GET</span> {API_BASE}/public/v1/listings/{'{id}'}</li>
            <li><span className="text-[#4A6B53]">GET</span> {API_BASE}/public/v1/categories</li>
          </ul>
        </section>

        <section className="bg-[#4A6B53] text-white rounded-xl p-6 mb-6" data-testid="starter-section">
          <h2 className="font-semibold text-lg mb-2">{tr('Gatavs Next.js veikals', 'Ready Next.js storefront')}</h2>
          <p className="text-sm text-white/80 mb-4">
            {tr('Lejupielādē gatavu Next.js projektu (meklēšana, filtri, produktu lapas), pievieno savu API atslēgu un palaid lokāli.',
                'Download a ready Next.js project (search, filters, product pages), add your API key and run locally.')}
          </p>
          <a href={`${API_BASE}/public/v1/starter/nextjs`} className="inline-block bg-white text-[#4A6B53] font-medium px-5 py-2.5 rounded-lg hover:bg-white/90" data-testid="download-starter-btn">
            {tr('Lejupielādēt starter (.zip)', 'Download starter (.zip)')}
          </a>
          <div className="flex flex-wrap gap-3 mt-3">
            <a href={`${API_BASE}/public/v1/starter/vue`} className="inline-block bg-white/10 border border-white/30 text-white text-sm px-4 py-2 rounded-lg hover:bg-white/20" data-testid="download-vue-btn">Vue (.zip)</a>
            <a href={`${API_BASE}/public/v1/starter/wordpress`} className="inline-block bg-white/10 border border-white/30 text-white text-sm px-4 py-2 rounded-lg hover:bg-white/20" data-testid="download-wp-btn">WordPress (.zip)</a>
          </div>
        </section>

        <section className="bg-white border border-[#E5E4E0] rounded-xl p-6 mb-6" data-testid="webhooks-section">
          <h2 className="font-semibold text-lg text-[#121212] mb-3">Webhooks</h2>
          <p className="text-sm text-[#5C5C5C] mb-3">
            {tr('Iestati webhook URL sadaļā Veikala dizains → Headless API. Notikumi: order.paid, order.shipped, inventory.low. Piemērs:',
                'Set a webhook URL under Shop Design → Headless API. Events: order.paid, order.shipped, inventory.low. Example:')}
          </p>
          <CodeBlock>{`POST <your-url>
X-KoLV-Event: order.paid
X-KoLV-Signature: sha256=<hmac>

{
  "event": "order.paid",
  "data": {
    "order_id": "...",
    "buyer_name": "...",
    "currency": "EUR",
    "items": [ ... ],
    "seller_total": 42.50
  },
  "ts": "2026-..."
}`}</CodeBlock>
          <p className="text-xs text-[#9CA3AF] mt-3">
            {tr('Pārbaudi parakstu: HMAC-SHA256(body, tavs webhook secret).', 'Verify the signature: HMAC-SHA256(body, your webhook secret).')}
          </p>
        </section>

        <section className="bg-white border border-[#E5E4E0] rounded-xl p-6 mb-6">
          <h2 className="font-semibold text-lg text-[#121212] mb-3">cURL</h2>
          <CodeBlock>{`curl "${API_BASE}/public/v1/listings" \\
  -H "X-API-Key: kolv_xxxxxxxxxxxx"`}</CodeBlock>
        </section>

        <section className="bg-white border border-[#E5E4E0] rounded-xl p-6 mb-6">
          <h2 className="font-semibold text-lg text-[#121212] mb-3">JavaScript (fetch)</h2>
          <CodeBlock>{`const res = await fetch(
  "${API_BASE}/public/v1/listings",
  { headers: { "X-API-Key": "kolv_xxxxxxxxxxxx" } }
);
const { count, listings } = await res.json();
console.log(count, listings);`}</CodeBlock>
        </section>

        <section className="bg-white border border-[#E5E4E0] rounded-xl p-6">
          <h2 className="font-semibold text-lg text-[#121212] mb-3">{tr('Atbildes piemērs', 'Example response')}</h2>
          <CodeBlock>{`{
  "count": 2,
  "listings": [
    {
      "id": "6a43...",
      "title": "Nīcas mājas medus",
      "price": 7.5,
      "category": "shop",
      "image": "https://...",
      "url": "/listing/nicas-majas-medus"
    }
  ]
}`}</CodeBlock>
          <p className="text-xs text-[#9CA3AF] mt-4">
            {tr('Piezīme: piekļuve tiek automātiski atsaukta, ja Premium Ultra plāns beidzas.',
                'Note: access is automatically revoked if the Premium Ultra plan lapses.')}
          </p>
        </section>
      </div>
    </div>
  );
}
